import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateDbtConnection } from '../../hooks/useDbtData';
import { dbtConfig } from '../../config/dbt';

/**
 * DbtConnectionForm Component
 * Form for creating a new dbt connection (Core or Cloud)
 */
const DbtConnectionForm = () => {
  const navigate = useNavigate();
  const createConnection = useCreateDbtConnection();

  const [connectionType, setConnectionType] = useState(dbtConfig.connectionTypes.CLOUD);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // Core fields
    project_path: '',
    profiles_path: '',
    target_name: 'dev',
    // Cloud fields
    account_id: '',
    project_id: '',
    api_token: '',
    environment_id: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (connectionType === dbtConfig.connectionTypes.CORE) {
      if (!formData.project_path.trim()) {
        newErrors.project_path = 'Project path is required for dbt Core';
      }
    } else {
      if (!formData.account_id.trim()) {
        newErrors.account_id = 'Account ID is required for dbt Cloud';
      }
      if (!formData.project_id.trim()) {
        newErrors.project_id = 'Project ID is required for dbt Cloud';
      }
      if (!formData.api_token.trim()) {
        newErrors.api_token = 'API token is required for dbt Cloud';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const connectionData = {
        name: formData.name,
        description: formData.description,
        connection_type: connectionType
      };

      if (connectionType === dbtConfig.connectionTypes.CORE) {
        connectionData.project_path = formData.project_path;
        connectionData.profiles_path = formData.profiles_path || null;
        connectionData.target_name = formData.target_name || 'dev';
      } else {
        connectionData.account_id = formData.account_id;
        connectionData.project_id = formData.project_id;
        connectionData.api_token_encrypted = formData.api_token; // Should be encrypted server-side
        connectionData.environment_id = formData.environment_id || null;
      }

      await createConnection.mutateAsync(connectionData);
      navigate('/dbt/connections');
    } catch (error) {
      console.error('Failed to create connection:', error);
      setErrors({ submit: error.message || 'Failed to create connection' });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white shadow-xl rounded-lg">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Create dbt Connection</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Connection Type Selection */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold text-secondary-900">Connection Type</span>
              </label>
              <div className="flex gap-4">
                <label className="cursor-pointer flex items-center gap-2 opacity-50 cursor-not-allowed" title="dbt Core requires file system access (not available in cloud deployment)">
                  <input
                    type="radio"
                    name="connectionType"
                    className="radio radio-primary"
                    checked={connectionType === dbtConfig.connectionTypes.CORE}
                    onChange={() => setConnectionType(dbtConfig.connectionTypes.CORE)}
                    disabled
                  />
                  <span className="text-secondary-500">dbt Core (Not available)</span>
                </label>
                <label className="cursor-pointer flex items-center gap-2">
                  <input
                    type="radio"
                    name="connectionType"
                    className="radio radio-primary"
                    checked={connectionType === dbtConfig.connectionTypes.CLOUD}
                    onChange={() => setConnectionType(dbtConfig.connectionTypes.CLOUD)}
                  />
                  <span className="text-secondary-900">dbt Cloud</span>
                </label>
              </div>
              <p className="text-sm text-secondary-500 mt-2">
                <strong>Note:</strong> dbt Core integration requires file system access and is only available for self-hosted deployments. Please use dbt Cloud for cloud-based deployments.
              </p>
            </div>

            {/* Common Fields */}
            <div className="form-control">
              <label className="label">
                <span className="label-text text-secondary-900">Name *</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input input-bordered bg-white text-secondary-900 ${errors.name ? 'input-error' : ''}`}
                placeholder="My dbt Project"
              />
              {errors.name && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.name}</span>
                </label>
              )}
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text text-secondary-900">Description</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="textarea textarea-bordered bg-white text-secondary-900"
                placeholder="Optional description"
                rows="3"
              />
            </div>

            {/* dbt Core Specific Fields */}
            {connectionType === dbtConfig.connectionTypes.CORE && (
              <>
                <div className="divider">dbt Core Configuration</div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Project Path *</span>
                  </label>
                  <input
                    type="text"
                    name="project_path"
                    value={formData.project_path}
                    onChange={handleChange}
                    className={`input input-bordered ${errors.project_path ? 'input-error' : ''}`}
                    placeholder="/path/to/dbt/project"
                  />
                  {errors.project_path && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.project_path}</span>
                    </label>
                  )}
                  <label className="label">
                    <span className="label-text-alt">Path to your dbt project directory</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Profiles Path</span>
                  </label>
                  <input
                    type="text"
                    name="profiles_path"
                    value={formData.profiles_path}
                    onChange={handleChange}
                    className="input input-bordered"
                    placeholder="~/.dbt (default)"
                  />
                  <label className="label">
                    <span className="label-text-alt">Optional: Path to profiles.yml</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Target Name</span>
                  </label>
                  <input
                    type="text"
                    name="target_name"
                    value={formData.target_name}
                    onChange={handleChange}
                    className="input input-bordered"
                    placeholder="dev"
                  />
                  <label className="label">
                    <span className="label-text-alt">Target environment (e.g., dev, prod)</span>
                  </label>
                </div>
              </>
            )}

            {/* dbt Cloud Specific Fields */}
            {connectionType === dbtConfig.connectionTypes.CLOUD && (
              <>
                <div className="divider text-secondary-700">dbt Cloud Configuration</div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-secondary-900">Account ID *</span>
                  </label>
                  <input
                    type="text"
                    name="account_id"
                    value={formData.account_id}
                    onChange={handleChange}
                    className={`input input-bordered bg-white text-secondary-900 ${errors.account_id ? 'input-error' : ''}`}
                    placeholder="12345"
                  />
                  {errors.account_id && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.account_id}</span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-secondary-900">Project ID *</span>
                  </label>
                  <input
                    type="text"
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleChange}
                    className={`input input-bordered bg-white text-secondary-900 ${errors.project_id ? 'input-error' : ''}`}
                    placeholder="67890"
                  />
                  {errors.project_id && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.project_id}</span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-secondary-900">API Token *</span>
                  </label>
                  <input
                    type="password"
                    name="api_token"
                    value={formData.api_token}
                    onChange={handleChange}
                    className={`input input-bordered bg-white text-secondary-900 ${errors.api_token ? 'input-error' : ''}`}
                    placeholder="Your dbt Cloud API token"
                  />
                  {errors.api_token && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.api_token}</span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-secondary-900">Environment ID</span>
                  </label>
                  <input
                    type="text"
                    name="environment_id"
                    value={formData.environment_id}
                    onChange={handleChange}
                    className="input input-bordered bg-white text-secondary-900"
                    placeholder="Optional"
                  />
                </div>
              </>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className="alert alert-error">
                <span>{errors.submit}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/dbt/connections')}
                className="btn btn-ghost"
                disabled={createConnection.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createConnection.isPending}
              >
                {createConnection.isPending ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Creating...
                  </>
                ) : (
                  'Create Connection'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DbtConnectionForm;
